import {
  FormFieldEffects,
  FormFieldType,
  type FormField,
  FormFieldChoice
} from '@/models/form';

interface LabelValue {
  label: string;
  value: string;
}

export const REQUIRED_VALUES: LabelValue[] = [
  {
    label: 'Not required',
    value: 'None'
  },
  {
    label: 'Non strict required',
    value: 'Non-Strict-Required'
  },
  {
    label: 'Strict required',
    value: 'Strict-Required'
  }
];

export type ConditionConfig = {
  value: string;
  operator: string;
  fieldId: string;

  //temporary form field holder
  formField?: FormField;
  choiceValue?: FormFieldChoice;
  effectOperator?: EffectOperator;
}

// Define the type for each effect's configuration
export type EffectConfig = {
  allOr: string;
  conditions: ConditionConfig[] | string[];
  message?: string;
  conditionId?: string;
}

export type DisableConfig = {
  // conditions: unknown[];
  disable?: boolean;
}

export type RequiredConfig = {
  strictness: string;

  required?: LabelValue;
}

export type DefaultConfig = {
  default?: string | number;
}

// Define the type for INITIAL_EFFECTS
export type Effects = {
  [key in FormFieldEffects]: EffectConfig | DisableConfig | RequiredConfig | DefaultConfig;
};

// Now, you can assign the type to INITIAL_EFFECTS
export const INITIAL_EFFECTS: Effects = {
  [FormFieldEffects.SHOW]: {
    allOr: 'All',
    conditions: []
  },
  [FormFieldEffects.VALID]: {
    allOr: 'All',
    conditions: [],
    message: '',
    conditionId: ''
  },
  [FormFieldEffects.WARN]: {
    allOr: 'All',
    conditions: [],
    message: '',
    conditionId: ''
  },
  [FormFieldEffects.ASSIGN]: {
    allOr: 'All',
    conditions: []
  },
  [FormFieldEffects.DISABLE]: {
    conditions: [],
    disable: false
  },
  [FormFieldEffects.REQUIRED]: {
    strictness: 'None',
    required: REQUIRED_VALUES[0]
  },
  [FormFieldEffects.DEFAULT]: {
    default: ''
  }
};

export const EFFECT_OPERATORS = [
  {
    operator: '>',
    label: 'greater than'
  },
  {
    operator: '<',
    label: 'less than'
  },
  {
    operator: '==',
    label: 'is'
  },
  {
    operator: '!=',
    label: 'is not'
  },
  {
    // Changed '⊂='(subset) to '⊃='(superset) for contains
    operator: '⊃=',
    label: 'contains'
  }
];

export type EffectOperator = typeof EFFECT_OPERATORS[number];

export const getOperatorConfig = (operator: string): EffectOperator | undefined =>
  Object.values(EFFECT_OPERATORS).find(op => op.operator === operator);

export const getEffectTitle = (effectType: FormFieldEffects) =>
  effectType === FormFieldEffects.SHOW ? {
    title: 'Branching Logic',
    iconStyle: 'fas',
    icon: 'eye'
  } : effectType === FormFieldEffects.VALID ? {
    title: 'Input Validation',
    iconStyle: 'fas',
    icon: 'do-not-enter'
  } : effectType === FormFieldEffects.WARN ? {
    title: 'Warn',
    iconStyle: 'fas',
    icon: 'exclamation-triangle'
  } : effectType === FormFieldEffects.DISABLE ? {
    title: 'Disable',
    iconStyle: 'fas',
    icon: 'ban'
  } : effectType === FormFieldEffects.REQUIRED ? {
    title: 'Required',
    iconStyle: 'fas',
    icon: 'asterisk'
  } : effectType === FormFieldEffects.DEFAULT ? {
    title: 'Default',
    iconStyle: 'fas',
    icon: 'thumbtack'
  } : null;

export const setupEffects = (formField: FormField, fields: FormField[]) => {
  const initialEffects = { ...JSON.parse(JSON.stringify(INITIAL_EFFECTS)) };

  (formField.conditions as Array<unknown>)?.forEach(fullCondition => {
    if (Array.isArray(fullCondition)) {
      const [type, ...restArguments] = fullCondition;
      if (!Object.values(FormFieldEffects).includes(type as FormFieldEffects)) {

        if (type.includes('Strict-Required')) {
          (initialEffects.Required as RequiredConfig).strictness = type;
  
          const foundValue = REQUIRED_VALUES.find(value => value.value === type);
  
          if (foundValue) {
            (initialEffects.Required as RequiredConfig).required = foundValue;
          }
        }

        // Unknow Field Effect
        return;
      }

      if (type === FormFieldEffects.ASSIGN) {
        (initialEffects.Assign as EffectConfig).conditions =
          restArguments as string[];
        return;
      }

      if (type === FormFieldEffects.DISABLE) {
        (initialEffects.Disable as DisableConfig).disable = true;
        return;
      }

      let message: string = '';
      let conditionId: string = '';
      let condition: unknown[] = [];

      if (restArguments.length === 1) {
        [condition] = restArguments;
      } else if (restArguments.length === 2) {
        [message, condition] = restArguments;
      } else if (restArguments.length === 3) {
        [message, conditionId, condition] = restArguments;
      }

      if (type === FormFieldEffects.VALID) {
        initialEffects.Valid.message = message as string;
        initialEffects.Valid.conditionId = conditionId as string;
      } else if (type === FormFieldEffects.WARN) {
        initialEffects.Warn.message = message as string;
        initialEffects.Warn.conditionId = conditionId as string;
      }

      const [operator] = condition;

      condition.forEach((element, index) => {
        if (Array.isArray(element)) {
          let op: string | null;
          if (operator === '&&') {
            op = 'All';
          } else if (operator === '||') {
            op = 'Or';
          } else {
            op = null;
          }

          if (type === FormFieldEffects.SHOW) {
            (initialEffects.Show as EffectConfig).allOr = op as string;
          } else if (type === FormFieldEffects.VALID) {
            (initialEffects.Valid as EffectConfig).allOr = op as string;
          } else if (type === FormFieldEffects.WARN) {
            (initialEffects.Warn as EffectConfig).allOr = op as string;
          }

          resolveFurtherConditions(element, initialEffects, type, index - 1, fields);
        }
      });

    }
  });

  (initialEffects.Default as DefaultConfig).default = formField.default;
  return initialEffects;
};

const resolveFurtherConditions = (
  nextCondition: unknown[],
  initialEffects: Effects,
  type: string,
  index: number,
  fields: FormField[]
) => {
  let operator, condition, actualIdentifier;
  if (nextCondition.length === 3) {
    [operator, condition] = nextCondition;

    if (typeof condition === 'string' && condition.indexOf('@') === -1) {
      [operator, condition, actualIdentifier] = nextCondition;
    } else {
      [operator, actualIdentifier, condition] = nextCondition;
    }
  }

  if (Array.isArray(condition) || Array.isArray(actualIdentifier)) {
    return;
  }

  if (Object.values(FormFieldEffects).includes(type as FormFieldEffects)) {
    const field = fields.find(fieldItem => fieldItem.identifier.toUpperCase() === (actualIdentifier! as string).replace('@', '').toUpperCase());

    if (type === FormFieldEffects.SHOW) {
      if ((initialEffects.Show as EffectConfig).conditions.length <= index) {
        const emptyCondition: ConditionConfig = {
          value: '',
          operator: '',
          fieldId: ''
        };
        ((initialEffects.Show as EffectConfig).conditions as ConditionConfig[]).push(emptyCondition);
      }
      
      
      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).value = condition as string;
      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).operator = operator as string;
      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).fieldId = actualIdentifier as string;

      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).formField = field;

      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).effectOperator = getOperatorConfig(operator as string);

      (
        (initialEffects.Show as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).choiceValue = field?.options.choices?.find(choice => choice.value == (condition! as string));

    } else if (type === FormFieldEffects.VALID) {
      if ((initialEffects.Valid as EffectConfig).conditions.length <= index) {
        const emptyCondition: ConditionConfig = {
          value: '',
          operator: '',
          fieldId: ''
        };
        ((initialEffects.Valid as EffectConfig).conditions as ConditionConfig[]).push(emptyCondition);
      }
      
      
      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).value = condition as string;
      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).operator = operator as string;
      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).fieldId = actualIdentifier as string;

      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).formField = field;

      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).effectOperator = getOperatorConfig(operator as string);

      (
        (initialEffects.Valid as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).choiceValue = field?.options.choices?.find(choice => choice.value == (condition! as string));
    } else if (type === FormFieldEffects.WARN) {
      if ((initialEffects.Warn as EffectConfig).conditions.length <= index) {
        const emptyCondition: ConditionConfig = {
          value: '',
          operator: '',
          fieldId: ''
        };
        ((initialEffects.Warn as EffectConfig).conditions as ConditionConfig[]).push(emptyCondition);
      }
      
      
      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).value = condition as string;
      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).operator = operator as string;
      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).fieldId = actualIdentifier as string;

      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).formField = field;

      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).effectOperator = getOperatorConfig(operator as string);

      (
        (initialEffects.Warn as EffectConfig).conditions[
          index
        ] as ConditionConfig
      ).choiceValue = field?.options.choices?.find(choice => choice.value == (condition! as string));
    }
  }
};

export const getConditionsArray = (effects: Effects, formField: FormField) => {
  const conditionsArray: unknown[] = [];

  if ((effects.Show as EffectConfig).conditions.length > 0) {
    const showArray = [];
    showArray.push('Show');
    const subShowArray = [];
    subShowArray.push((effects.Show as EffectConfig).allOr === 'All' ? '&&' : '||');

    for (const condition of ((effects.Show as EffectConfig).conditions as ConditionConfig[])) {
      const value = (condition.formField?.type === FormFieldType.CHOICE || condition.formField?.type === FormFieldType.DROPDOWN) ?
        condition.choiceValue?.value : condition.value;
      const conditionArray = [condition.effectOperator?.operator, `@${condition.formField?.identifier.toUpperCase()}`, value];
      subShowArray.push(conditionArray);
    }

    showArray.push(subShowArray);
    conditionsArray.push(showArray);
  }

  if ((effects.Valid as EffectConfig).conditions.length > 0) {
    const validArray = [];
    validArray.push('Valid');
    validArray.push((effects.Valid as EffectConfig).message);
    if ((effects.Valid as EffectConfig).conditionId) {
      validArray.push((effects.Valid as EffectConfig).conditionId);
    }
    const subValidArray = [];
    subValidArray.push((effects.Valid as EffectConfig).allOr === 'All' ? '&&' : '||');

    for (const condition of ((effects.Valid as EffectConfig).conditions as ConditionConfig[])) {
      const value = (condition.formField?.type === FormFieldType.CHOICE || condition.formField?.type === FormFieldType.DROPDOWN) ?
        condition.choiceValue?.value : condition.value;
      const conditionArray = [condition.effectOperator?.operator, `@${condition.formField?.identifier.toUpperCase()}`, value];
      subValidArray.push(conditionArray);
    }

    validArray.push(subValidArray);
    conditionsArray.push(validArray);
  }

  if ((effects.Warn as EffectConfig).conditions.length > 0) {
    const warnArray = [];
    warnArray.push('Warn');
    warnArray.push((effects.Warn as EffectConfig).message);
    if ((effects.Warn as EffectConfig).conditionId) {
      warnArray.push((effects.Warn as EffectConfig).conditionId);
    }
    const subWarnArray = [];
    subWarnArray.push((effects.Warn as EffectConfig).allOr === 'All' ? '&&' : '||');

    for (const condition of ((effects.Warn as EffectConfig).conditions as ConditionConfig[])) {
      const value = (condition.formField?.type === FormFieldType.CHOICE || condition.formField?.type === FormFieldType.DROPDOWN) ?
        condition.choiceValue?.value : condition.value;
      const conditionArray = [condition.effectOperator?.operator, `@${condition.formField?.identifier.toUpperCase()}`, value];
      subWarnArray.push(conditionArray);
    }

    warnArray.push(subWarnArray);
    conditionsArray.push(warnArray);
  }

  if ((effects.Disable as DisableConfig).disable) {
    conditionsArray.push(['Disable']);
  }

  if ((effects.Required as RequiredConfig).required?.value == REQUIRED_VALUES[1].value ||
    (effects.Required as RequiredConfig).required?.value == REQUIRED_VALUES[2].value) {
    conditionsArray.push([(effects.Required as RequiredConfig).required?.value]);
  }

  if ((effects.Default as DefaultConfig).default) {
    formField.default = (effects.Default as DefaultConfig).default as string;
  } else {
    formField.default = '';
  }

  return conditionsArray;
};
